import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudDownload, Copy, Loader2, MoonStar } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/auth";

const KEY = "tea_live_url";
const MODES = [["add", "Add missing only", "Adds live records preview doesn't have. Nothing in preview is changed or removed."],
  ["mirror", "Mirror live exactly", "Also updates changed records and removes preview records live doesn't have (QA test logins are kept)."]];
const input = "bg-black/40 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:border-nex-gold/60";

const KIND = { par_partners: ["partner", "partners", "partner"], users: ["login", "logins", "login"], crm_contacts: ["contact", "contacts", "contact"] };
const sentence = (skips) => {
  const em = skips.filter((s) => s.field === "email");
  const parts = Object.entries(em.reduce((m, s) => ({ ...m, [s.collection]: (m[s.collection] || 0) + 1 }), {})).map(([c, n]) => {
    const [one, many, other] = KIND[c] || ["record", "records", "record"];
    return `${n} ${n === 1 ? one : many} because ${n === 1 ? "its" : "their"} email already belongs to another ${other} in preview`;
  });
  const rest = skips.length - em.length;
  if (rest) parts.push(`${rest} other record${rest === 1 ? "" : "s"} because ${rest === 1 ? "its" : "their"} unique value already exists on another preview record`);
  return `Skipped ${parts.join("; ")}. Skipped records were left untouched.`;
};

function SkipList({ skips, total }) {
  const copy = () => navigator.clipboard.writeText(skips.map((s) => [s.collection, s.name, s.value, `live ${s.live_id}`, `preview ${s.preview_id || "—"}`].join("\t")).join("\n"))
    .then(() => toast.success("Skipped list copied")).catch(() => toast.error("Couldn't copy"));
  return (
    <details className="mt-2 rounded-sm border border-amber-400/30 bg-amber-500/[0.06] p-2" data-testid="data-sync-skips">
      <summary className="cursor-pointer text-amber-200" data-testid="data-sync-skips-summary">{sentence(skips)}{total > skips.length ? ` Showing the first ${skips.length} of ${total}.` : ""}</summary>
      <div className="mt-2 flex justify-end"><button type="button" onClick={copy} data-testid="data-sync-skips-copy" className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-white/15 text-[11px] text-slate-200 hover:text-nex-gold"><Copy size={12} /> Copy list</button></div>
      <div className="mt-1 max-h-60 overflow-y-auto space-y-1">
        {skips.map((s, i) => (
          <div key={i} data-testid={`data-sync-skip-${i}`} className="grid grid-cols-[auto_1fr] gap-x-2 text-[11px] border-b border-white/5 pb-1">
            <span className="font-mono text-slate-500">{s.collection}</span>
            <span className="text-slate-200">{s.name} · <span className="text-amber-200">{s.value}</span>
              <span className="block text-slate-400 font-mono">live id {s.live_id} · clashes with preview id {s.preview_id || "—"}</span></span>
          </div>))}
      </div>
    </details>
  );
}

function Nightly({ n, onChange }) {
  const flip = async () => {
    try { await apiClient.put("/admin/data-sync/nightly", { enabled: !n.enabled }); onChange(); toast.success(!n.enabled ? "Nightly mirror on" : "Nightly mirror off"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/10" data-testid="data-sync-nightly">
      <MoonStar size={14} className="text-nex-gold" />
      <label className="inline-flex items-center gap-2 text-xs text-slate-200 cursor-pointer">
        <input type="checkbox" checked={!!n.enabled} onChange={flip} data-testid="data-sync-nightly-toggle" className="accent-amber-400" />
        Mirror live every night at 2:00 AM Eastern
      </label>
      <span className={`text-[11px] ${n.ready ? "text-emerald-300" : "text-amber-200"}`} data-testid="data-sync-nightly-ready">
        {n.ready ? `Live sign-in saved · ${n.live_url}` : "Live address and sign-in not saved yet"}
      </span>
      {n.enabled && <span className="text-[11px] text-slate-400 basis-full">While this is on, preview skips its own Monday rent / case management charges — they arrive from live.</span>}
    </div>
  );
}

/** Pull live data into this (preview) database. Safe to run again any time; live is only read. */
export function DataSyncPanel() {
  const [f, setF] = useState({ live_url: localStorage.getItem(KEY) || "", identifier: "", pin: "", mode: "add" });
  const [d, setD] = useState({ runs: [], nightly: {} });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiClient.get("/admin/data-sync/runs").then(({ data }) => setD(data)).catch(() => {}), []);
  const last = d.runs[0];
  const running = last?.status === "running";
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [running, load]);
  const go = async () => {
    if (f.mode === "mirror" && !window.confirm("Mirror will update and remove preview records so preview matches live exactly. Continue?")) return;
    setBusy(true);
    try {
      localStorage.setItem(KEY, f.live_url.trim());
      await apiClient.post("/admin/data-sync/pull", f);
      setF((x) => ({ ...x, pin: "" }));
      toast.success("Sync started");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-industrial corner-tick p-4 mb-6 space-y-3" data-testid="data-sync-panel">
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-nex-gold"><CloudDownload size={14} /> Copy live data into preview</div>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {MODES.map(([k, l, hint]) => (
          <button key={k} type="button" role="radio" aria-checked={f.mode === k} onClick={() => setF({ ...f, mode: k })} data-testid={`data-sync-mode-${k}`} title={hint}
            className={`px-3 py-1.5 rounded-sm border text-xs transition-colors ${f.mode === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/15 text-slate-400 hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-slate-400" data-testid="data-sync-mode-hint">{MODES.find((m) => m[0] === f.mode)[2]} The live app is only read.</p>
      <div className="flex flex-wrap gap-2">
        <input className={`${input} flex-1 min-w-[220px]`} placeholder="Live app address (https://…)" value={f.live_url} onChange={(e) => setF({ ...f, live_url: e.target.value })} data-testid="data-sync-url" />
        <input className={input} placeholder="Live super admin email" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} data-testid="data-sync-email" />
        <input className={`${input} w-28`} type="password" placeholder="PIN" value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value })} data-testid="data-sync-pin" />
        <button type="button" onClick={go} disabled={busy || running || !f.live_url || !f.identifier || !f.pin} data-testid="data-sync-start" className="tick-btn-secondary text-xs !py-2 !px-3 disabled:opacity-40">
          {busy || running ? <Loader2 size={13} className="animate-spin" /> : <CloudDownload size={13} />} {running ? "Syncing…" : "Pull from live"}
        </button>
      </div>
      {last && (
        <div className="text-xs text-slate-300" data-testid="data-sync-status">
          Last run ({last.mode === "mirror" ? "mirror" : "add missing"}{last.by === "nightly" ? ", nightly" : ""}): <b data-testid="data-sync-status-label" className={last.status === "failed" ? "text-red-300" : last.status === "done" ? "text-emerald-300" : "text-amber-200"}>{last.status === "done_with_skips" ? "finished with skips" : last.status}</b>
          {" · "}{new Date(last.started_at).toLocaleString()} · added {(last.inserted || 0).toLocaleString()}
          {last.mode === "mirror" && <> · updated {(last.updated || 0).toLocaleString()} · removed {(last.removed || 0).toLocaleString()}</>}
          {last.mode !== "mirror" && <> · already here {(last.skipped || 0).toLocaleString()}</>}
          {" · "}<span data-testid="data-sync-dup-count" className={last.dup_skipped ? "text-amber-200" : ""}>Skipped (duplicate email) {(last.skips || []).filter((s) => s.field === "email").length.toLocaleString()}</span>
          {(last.skips || []).some((s) => s.field !== "email") && <> · <span data-testid="data-sync-dup-other-count" className="text-amber-200">Skipped (other duplicate) {(last.skips || []).filter((s) => s.field !== "email").length.toLocaleString()}</span></>}
          {last.error && <span className="block text-red-300" data-testid="data-sync-error">{last.error}</span>}
          {last.skips?.length > 0 && <SkipList skips={last.skips} total={last.dup_skipped || last.skips.length} />}
        </div>
      )}
      <Nightly n={d.nightly || {}} onChange={load} />
    </div>
  );
}
